import { Minus, Plus, Scan, ZoomIn, ZoomOut } from "lucide-react";
import { useRef, useState } from "react";
import { METERS_TO_PX, PLAN_OFFSET } from "./data";
import type { AssetKind, Furniture, Room, Wall } from "./types";

type Props = {
  walls: Wall[];
  rooms: Room[];
  furniture: Furniture[];
  selectedWallId: string | null;
  selectedFurnitureId: string | null;
  onSelectWall: (id: string | null) => void;
  onSelectFurniture: (id: string | null) => void;
  onChangeWall: (wall: Wall) => void;
  onMoveFurniture: (id: string, x: number, y: number) => void;
  onAddAsset: (kind: AssetKind, x: number, y: number) => void;
};

const sx = (x: number) => PLAN_OFFSET.x + x * METERS_TO_PX;
const sy = (y: number) => PLAN_OFFSET.y + y * METERS_TO_PX;

function FurnitureGlyph({ item }: { item: Furniture }) {
  const width = item.width * METERS_TO_PX;
  const depth = item.depth * METERS_TO_PX;
  if (item.kind === "plant") {
    return (
      <>
        <circle r={Math.min(width, depth) * 0.46} fill={item.color} />
        <path d="M-8 1 Q-2-13 1-1 Q8-12 8 4 Q0 10-8 1Z" fill="#79A082" />
      </>
    );
  }
  if (item.kind === "table") {
    return (
      <>
        <rect x={-width / 2} y={-depth / 2} width={width} height={depth} rx="7" fill={item.color} />
        {[-0.34, 0.34].map((dx) =>
          [-0.65, 0.65].map((dy) => (
            <rect key={`${dx}-${dy}`} x={dx * width - 7} y={dy * depth - 5} width="14" height="10" rx="4" fill="#E7D9C6" />
          )),
        )}
      </>
    );
  }
  if (item.kind === "bed") {
    return (
      <>
        <rect x={-width / 2} y={-depth / 2} width={width} height={depth} rx="8" fill={item.color} />
        <rect x={-width * 0.39} y={-depth * 0.4} width={width * 0.34} height={depth * 0.22} rx="5" fill="#E9E5DE" />
        <rect x={width * 0.05} y={-depth * 0.4} width={width * 0.34} height={depth * 0.22} rx="5" fill="#E9E5DE" />
        <path d={`M${-width / 2} ${depth * 0.07} H${width / 2}`} stroke="#EFF0EC" strokeWidth="3" />
      </>
    );
  }
  return (
    <>
      <rect x={-width / 2} y={-depth / 2} width={width} height={depth} rx={item.kind === "sofa" ? 10 : 5} fill={item.color} />
      {item.kind === "sofa" && (
        <>
          <rect x={-width * 0.42} y={-depth * 0.32} width={width * 0.37} height={depth * 0.62} rx="6" fill="#709586" />
          <rect x={width * 0.05} y={-depth * 0.32} width={width * 0.37} height={depth * 0.62} rx="6" fill="#709586" />
        </>
      )}
      {item.kind === "chair" && <rect x={-width * 0.32} y={-depth * 0.3} width={width * 0.64} height={depth * 0.6} rx="9" fill="#DF9972" />}
      {item.kind === "cabinet" && <path d={`M0 ${-depth / 2} V${depth / 2}`} stroke="#A7834F" strokeWidth="2" />}
    </>
  );
}

export function Plan2D({
  walls,
  rooms,
  furniture,
  selectedWallId,
  selectedFurnitureId,
  onSelectWall,
  onSelectFurniture,
  onChangeWall,
  onMoveFurniture,
  onAddAsset,
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [zoom, setZoom] = useState(1);
  const [dragFurniture, setDragFurniture] = useState<string | null>(null);
  const [dragWall, setDragWall] = useState<{ id: string; end: "start" | "end" } | null>(null);

  const toPlan = (clientX: number, clientY: number) => {
    const svg = svgRef.current!;
    const point = svg.createSVGPoint();
    point.x = clientX;
    point.y = clientY;
    const local = point.matrixTransform(svg.getScreenCTM()!.inverse());
    return {
      x: Math.max(-0.5, Math.min(16, (local.x - PLAN_OFFSET.x) / METERS_TO_PX)),
      y: Math.max(-0.5, Math.min(10.5, (local.y - PLAN_OFFSET.y) / METERS_TO_PX)),
    };
  };

  const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!dragFurniture && !dragWall) return;
    const point = toPlan(event.clientX, event.clientY);
    if (dragFurniture) onMoveFurniture(dragFurniture, point.x, point.y);
    if (dragWall) {
      const wall = walls.find((entry) => entry.id === dragWall.id);
      if (!wall) return;
      const snapped = { x: Math.round(point.x * 20) / 20, y: Math.round(point.y * 20) / 20 };
      onChangeWall({
        ...wall,
        ...(dragWall.end === "start"
          ? { x1: snapped.x, y1: snapped.y }
          : { x2: snapped.x, y2: snapped.y }),
        label: undefined,
      });
    }
  };

  const stopDragging = () => {
    setDragFurniture(null);
    setDragWall(null);
  };

  return (
    <div
      className="plan-surface"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        const kind = event.dataTransfer.getData("asset-kind") as AssetKind;
        if (!kind) return;
        const point = toPlan(event.clientX, event.clientY);
        onAddAsset(kind, point.x, point.y);
      }}
    >
      <svg
        ref={svgRef}
        className="plan-svg"
        viewBox={`0 0 ${820 / zoom} ${570 / zoom}`}
        role="application"
        aria-label="Editable two-dimensional floor plan"
        onPointerMove={handlePointerMove}
        onPointerUp={stopDragging}
        onPointerLeave={stopDragging}
        onClick={() => {
          onSelectWall(null);
          onSelectFurniture(null);
        }}
      >
        <defs>
          <pattern id="minor-grid" width={METERS_TO_PX / 2} height={METERS_TO_PX / 2} patternUnits="userSpaceOnUse">
            <path d={`M ${METERS_TO_PX / 2} 0 L 0 0 0 ${METERS_TO_PX / 2}`} fill="none" stroke="#DCD8CE" strokeWidth="0.65" />
          </pattern>
          <pattern id="grid" width={METERS_TO_PX} height={METERS_TO_PX} patternUnits="userSpaceOnUse">
            <rect width={METERS_TO_PX} height={METERS_TO_PX} fill="url(#minor-grid)" />
            <path d={`M ${METERS_TO_PX} 0 L 0 0 0 ${METERS_TO_PX}`} fill="none" stroke="#CFC9BD" strokeWidth="0.8" />
          </pattern>
          <filter id="furniture-shadow" x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#1D2B27" floodOpacity="0.16" />
          </filter>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />

        {rooms.map((room) => (
          <g key={room.id} pointerEvents="none">
            <rect
              x={sx(room.x)}
              y={sy(room.y)}
              width={room.width * METERS_TO_PX}
              height={room.depth * METERS_TO_PX}
              fill={room.tone}
              opacity="0.72"
            />
            <text
              x={sx(room.x + room.width / 2)}
              y={sy(room.y + room.depth / 2)}
              textAnchor="middle"
              className="room-label"
            >
              {room.name}
            </text>
            <text
              x={sx(room.x + room.width / 2)}
              y={sy(room.y + room.depth / 2) + 17}
              textAnchor="middle"
              className="room-area"
            >
              {(room.width * room.depth).toFixed(1)} m²
            </text>
          </g>
        ))}

        {walls.map((wall) => {
          const selected = wall.id === selectedWallId;
          const x1 = sx(wall.x1);
          const y1 = sy(wall.y1);
          const x2 = sx(wall.x2);
          const y2 = sy(wall.y2);
          const midX = (x1 + x2) / 2;
          const midY = (y1 + y2) / 2;
          const horizontal = Math.abs(wall.x2 - wall.x1) > Math.abs(wall.y2 - wall.y1);
          const geometricLength = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1);
          const label = wall.label ?? geometricLength.toFixed(2);
          return (
            <g key={wall.id} onClick={(event) => event.stopPropagation()}>
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={selected ? "#D9663D" : "#26312D"}
                strokeWidth={wall.thickness * METERS_TO_PX}
                strokeLinecap="square"
                className="wall-line"
                onClick={() => onSelectWall(wall.id)}
              />
              <g
                className={`dimension-chip ${selected ? "is-selected" : ""}`}
                transform={`translate(${midX + (horizontal ? 0 : 17)}, ${midY + (horizontal ? -15 : 0)})`}
                onClick={() => onSelectWall(wall.id)}
              >
                <rect x="-25" y="-11" width="50" height="22" rx="11" />
                <text textAnchor="middle" dominantBaseline="middle">{label} m</text>
              </g>
              {selected && (
                <>
                  <circle
                    cx={x1}
                    cy={y1}
                    r="7"
                    className="wall-handle"
                    onPointerDown={(event) => {
                      event.stopPropagation();
                      setDragWall({ id: wall.id, end: "start" });
                    }}
                  />
                  <circle
                    cx={x2}
                    cy={y2}
                    r="7"
                    className="wall-handle"
                    onPointerDown={(event) => {
                      event.stopPropagation();
                      setDragWall({ id: wall.id, end: "end" });
                    }}
                  />
                </>
              )}
            </g>
          );
        })}

        {furniture.map((item) => (
          <g
            key={item.id}
            transform={`translate(${sx(item.x)}, ${sy(item.y)}) rotate(${item.rotation})`}
            className={`furniture-object ${item.id === selectedFurnitureId ? "is-selected" : ""}`}
            filter="url(#furniture-shadow)"
            onClick={(event) => {
              event.stopPropagation();
              onSelectFurniture(item.id);
              onSelectWall(null);
            }}
            onPointerDown={(event) => {
              event.stopPropagation();
              onSelectFurniture(item.id);
              setDragFurniture(item.id);
            }}
          >
            <FurnitureGlyph item={item} />
            {item.id === selectedFurnitureId && (
              <rect
                x={(-item.width * METERS_TO_PX) / 2 - 6}
                y={(-item.depth * METERS_TO_PX) / 2 - 6}
                width={item.width * METERS_TO_PX + 12}
                height={item.depth * METERS_TO_PX + 12}
                rx="8"
                fill="none"
                stroke="#D9663D"
                strokeWidth="2"
                strokeDasharray="5 4"
              />
            )}
          </g>
        ))}
      </svg>

      <div className="canvas-tools" aria-label="Canvas controls">
        <button onClick={() => setZoom((value) => Math.min(1.35, value + 0.12))} aria-label="Zoom in"><ZoomIn size={17} /></button>
        <button onClick={() => setZoom(1)} aria-label="Fit plan"><Scan size={17} /></button>
        <button onClick={() => setZoom((value) => Math.max(0.72, value - 0.12))} aria-label="Zoom out"><ZoomOut size={17} /></button>
      </div>
      <div className="scale-badge"><Minus size={15} /> 1 square = 0.5 m <Plus size={15} /></div>
    </div>
  );
}
